import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import AppTable from '../../../components/AppTable/AppTable';
import { setPagination } from '../../../redux/Actions/TableAction';
import { searchStock, searchStockTransaction } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';
import TableSearch from '../../../components/TableSearch/TableSearch';
import StockReceiving from '../Warehouse/StockReceiving';
import TransferStock from '../Warehouse/TransferStock';
import StockTransferHistory from '../Warehouse/StockTransferHistory';

function Retails() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Local state for loading and error handling
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Modal state
    const [showReceivingModal, setShowReceivingModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [receivingCount, setReceivingCount] = useState(0);
    const [transferCount, setTransferCount] = useState(0);

    // Permission checks
    const canReadRetails = true;
    const canManageRetails = true;

    // Initialize with empty array to prevent map errors
    useEffect(() => {
        if (!Array.isArray(ApiReducer.apiJson)) {
            dispatch(setApiJson([]));
        }
    }, []);

    // Memoized function to search retail stock
    const searchRetailStock = useCallback(async (searchKey, searchValue) => {
        setIsLoading(true);
        setIsSearching(true);
        setError(null);

        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                type: 'retail',
                place:'salon' // Filter for retail products only
            };

            // Only add search key/value if both are provided
            if (searchKey && searchValue) {
                // Handle product-related searches with population
                const productFields = ['productName', 'brand', 'productType'];
                const stockFields = ['stockIn', 'stockOut', 'stockAlert', 'place', 'stockLocation'];
                
                if (productFields.includes(searchKey)) {
                    // For product fields, search in populated productId field
                    searchObj[`productId.${searchKey}`] = {
                        $regex: searchValue,
                        $options: 'i'
                    };
                } else if (stockFields.includes(searchKey)) {
                    // Handle numeric stock fields
                    const numericFields = ['stockIn', 'stockOut', 'stockAlert'];
                    if (numericFields.includes(searchKey)) {
                        searchObj[searchKey] = parseFloat(searchValue) || 0;
                    } else {
                        // For text fields like place, stockLocation
                        searchObj[searchKey] = {
                            $regex: searchValue,
                            $options: 'i'
                        };
                    }
                }
            }

            const json = {
                page: TableDataReducer.pagination.page,
                limit: TableDataReducer.pagination.limit,
                search: searchObj,
                populate: ['productId'] // Ensure product data is populated
            };

            const res = await HitApi(json, searchStock);

            // Handle response
            let stockData = [];
            let totalCount = 0;

            if (res?.statusCode === 200) {
                // Stock API response structure
                stockData = res?.data?.docs || res?.stocks || res?.result || [];
                totalCount = res?.data?.totalDocs || res?.total || 0;

                // Ensure we have an array
                if (!Array.isArray(stockData)) {
                    console.warn("Stock API returned non-array data:", stockData);
                    stockData = [];
                }

                // Transform stock data for retail display
                const retailData = stockData.map(stock => ({
                    _id: stock._id,
                    productId: stock.productId?._id || stock.productId,
                    productName: stock.productId?.productName || 'Unknown Product',
                    brand: stock.productId?.brand || '',
                    productType: stock.productId?.productType || '',
                    productImageUrl: stock.productId?.productImageUrl || '',
                    quantity: stock.stockIn - stock.stockOut || 0,
                    costPrice: stock.productId?.costPrice || 0,
                    sellPrice: stock.productId?.sellPrice || 0,
                    mrp: stock.productId?.mrp || 0,
                    vendor: stock.productId?.vendor || stock.type || '',
                    type: stock.type || 'retail',
                    place: stock.place || 'warehouse',
                    stockLocation: stock.stockLocation || 'warehouse',
                    stockAlert: stock.stockAlert || 0,
                    stockIn: stock.stockIn || 0,
                    stockOut: stock.stockOut || 0,
                    createdAt: stock.createdAt,
                    updatedAt: stock.updatedAt
                }));

                dispatch(setApiJson(retailData));

                // Update pagination data in Redux
                const paginationData = {
                    total: totalCount,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
            } else {
                const errorMessage = res?.message || res?.error || 'Failed to fetch retail stock';
                console.error("Stock API Error:", errorMessage);
                setError(errorMessage);
                dispatch(setApiJson([]));
                toast.error('Failed to fetch retail stock. Please try again.');
            }
        } catch (error) {
            console.error("Error fetching retail stock:", error);
            setError('Failed to fetch retail stock. Please check your connection and try again.');
            dispatch(setApiJson([]));
            toast.error('Error fetching retail stock. Please try again.');
        } finally {
            setIsLoading(false);
            setIsSearching(false);
        }
    }, [dispatch, TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

    // Function to fetch receiving count (items transferred TO salon FROM warehouse for retail products)
    const fetchReceivingCount = useCallback(async () => {
        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                status: 'transferd',
                transferFrom: 'warehouse', // Items coming from warehouse to salon
                type: 'retail' // Only retail products
            };

            const json = {
                page: 1,
                limit: 1, // We only need the count, not the actual data
                search: searchObj
            };

            const res = await HitApi(json, searchStockTransaction);

            if (res?.statusCode === 200) {
                const totalCount = res?.data?.totalDocs || res?.total || 0;
                setReceivingCount(totalCount);
            }
        } catch (error) {
            console.error("Error fetching receiving count:", error);
            // Don't show error toast for count fetch failures
        }
    }, []);

    // Function to fetch transfer count (for retail products from salon)
    const fetchTransferCount = useCallback(async () => {
        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                status: 'transferd',
                transferFrom: 'salon', // Only transfers from salon
                type: 'retail' // Only retail products
            };

            const json = {
                page: 1,
                limit: 1, // We only need the count, not the actual data
                search: searchObj
            };

            const res = await HitApi(json, searchStockTransaction);

            if (res?.statusCode === 200) {
                const totalCount = res?.data?.totalDocs || res?.total || 0;
                setTransferCount(totalCount);
            }
        } catch (error) {
            console.error("Error fetching transfer count:", error);
            // Don't show error toast for count fetch failures
        }
    }, []);

    useEffect(() => {
        if (canReadRetails) {
            searchRetailStock();
            fetchReceivingCount(); // Fetch receiving count on load
            fetchTransferCount(); // Fetch transfer count on load
        }
    }, [searchRetailStock, canReadRetails, fetchReceivingCount, fetchTransferCount]);

    // Search functionality
    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }

        let searchValue = valueSearched.trim();

        // Convert numeric fields to numbers for stock fields
        const numericFields = ['stockIn', 'stockOut', 'stockAlert', 'quantity'];
        if (numericFields.includes(optionSelect)) {
            const numericValue = parseFloat(searchValue);
            if (isNaN(numericValue)) {
                toast.error('Please enter a valid number for numeric fields');
                return;
            }
            searchValue = numericValue;
        }

        searchRetailStock(optionSelect, searchValue);
    };

    // Search options for retail stock
    const options = [
        { label: 'Product Name', value: 'productName' },
        { label: 'Brand', value: 'brand' },
        { label: 'Product Type', value: 'productType' },
        { label: 'Stock In', value: 'stockIn' },
        { label: 'Stock Out', value: 'stockOut' },
        { label: 'Stock Alert Level', value: 'stockAlert' },
        { label: 'Storage Place', value: 'place' },
        { label: 'Stock Location (Warehouse/Salon)', value: 'stockLocation' },
    ];

    // Format currency with proper validation
    const formatCurrency = useCallback((amount) => {
        if (amount === null || amount === undefined || amount === '' || isNaN(amount)) {
            return '₹0.00';
        }
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        if (isNaN(numAmount)) return '₹0.00';
        return `₹${numAmount.toFixed(2)}`;
    }, []);

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    // Define retail stock table headers
    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "start",
            priority: 1,
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Product",
            key: "productName",
            priority: 1,
            render: (value, row) => (
                <div className="flex items-start">
                    {row.productImageUrl ? (
                        <img
                            src={row.productImageUrl}
                            alt={value || 'Product'}
                            className="w-10 h-10 rounded-lg mr-3 object-cover flex-shrink-0"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div className="w-10 h-10 rounded-lg bg-orange-200 flex items-center justify-center mr-3 flex-shrink-0"
                        style={{ display: row.productImageUrl ? 'none' : 'flex' }}>
                        <span className="text-lg">🛍️</span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate" title={value}>
                            {value || 'Unnamed Product'}
                        </div>
                        {row.brand && (
                            <div className="text-sm text-gray-500 truncate mt-1" title={row.brand}>
                                {row.brand}
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: "Quantity",
            key: "quantity",
            align: "center",
            priority: 1,
            render: (value) => (
                <span className={`font-medium px-2 py-1 rounded text-sm ${
                    value <= 0 ? 'bg-red-100 text-red-800' : 
                    value < 10 ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-green-100 text-green-800'
                }`}>
                    {value || 0}
                </span>
            )
        },
        {
            title: "Stock In",
            key: "stockIn",
            align: "center",
            priority: 2,
            render: (value) => (
                <span className="font-medium text-blue-600">{value || 0}</span>
            )
        },
        {
            title: "Stock Out",
            key: "stockOut",
            align: "center",
            priority: 2,
            render: (value) => (
                <span className="font-medium text-orange-600">{value || 0}</span>
            )
        },
        {
            title: "Cost Price",
            key: "costPrice",
            align: "start",
            priority: 3,
            render: (value) => (
                <span className="font-medium">{formatCurrency(value)}</span>
            )
        },
        {
            title: "Sell Price",
            key: "sellPrice",
            align: "start",
            priority: 2,
            render: (value) => (
                <span className="font-medium text-green-600">{formatCurrency(value)}</span>
            )
        },
        {
            title: "MRP",
            key: "mrp",
            align: "start",
            priority: 4,
            render: (value) => (
                <span className="font-medium">{formatCurrency(value)}</span>
            )
        },
        {
            title: "Type",
            key: "productType",
            priority: 3,
            render: (value) => (
                <span className="capitalize">
                    {value ? value.replace(/[-_]/g, ' ') : 'N/A'}
                </span>
            )
        },
        {
            title: "Place",
            key: "place",
            priority: 4,
            render: (value) => (
                <span className="capitalize" title={value}>
                    {value || 'Warehouse'}
                </span>
            )
        },
        {
            title: "Location",
            key: "stockLocation",
            priority: 3,
            render: (value) => (
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    value === 'warehouse' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                }`}>
                    {value === 'warehouse' ? 'Warehouse' : 'Salon'}
                </span>
            )
        },
        {
            title: "Alert Level",
            key: "stockAlert",
            align: "center",
            priority: 4,
            render: (value, row) => (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                    row.quantity <= value ? 'bg-red-100 text-red-800' : 
                    'bg-gray-100 text-gray-800'
                }`}>
                    {value || 0}
                </span>
            )
        }
    ];

    const retailData = Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : [];

    const handleClearSearch = () => {
        setOptionSelect('');
        setValueSearched('');
        searchRetailStock();
    };

    return (
        <div className="p-5">
            <PageHeader
                title="Retail Products Stock"
                description="View and search retail products in inventory"
            />

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-medium">Error Loading Retail Stock</h4>
                            <p className="mt-1">{error}</p>
                        </div>
                        <button
                            onClick={() => searchRetailStock()}
                            disabled={isLoading}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            {isLoading ? 'Loading...' : 'Retry'}
                        </button>
                    </div>
                </div>
            )}

            {!canReadRetails ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading permissions...</div>
                </div>
            ) : (
                <>
                    {/* Action Buttons */}
                    {canManageRetails && (
                        <div className="mb-4 flex space-x-3">
                            <button
                                onClick={() => navigate("/warehouse/add?location=salon&type=retail")}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                                disabled={isLoading}
                            >
                                Add to Retails
                            </button>
                            <button
                                onClick={() => setShowTransferModal(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                disabled={isLoading}
                            >
                                Transfer Stock
                            </button>
                            <button
                                onClick={() => setShowHistoryModal(true)}
                                className="relative px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                                disabled={isLoading}
                            >
                                Check Stock Transfer
                                {transferCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                                        {transferCount > 99 ? '99+' : transferCount}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setShowReceivingModal(true)}
                                className="relative px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                                disabled={isLoading}
                            >
                                Stock Receiving
                                {receivingCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                                        {receivingCount > 99 ? '99+' : receivingCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    )}

                    <AppTable
                        TH={tableHeaders}
                        TD={retailData}
                        isLoading={isLoading || isSearching}
                        emptyMessage={error ? "Failed to load retail stock data. Please try again." : "No retail products found in stock."}
                        TableSearch={
                            <TableSearch
                                options={options}
                                onClick={handleSearch}
                                optionSelect={optionSelect}
                                setOptionSelect={setOptionSelect}
                                setValueSearched={setValueSearched}
                                valueSearched={valueSearched}
                                onClear={handleClearSearch}
                            />
                        }
                    />
                </>
            )}

            {/* Transfer Stock Modal */}
            {showTransferModal && (
                <TransferStock 
                    isModal={true}
                    onClose={() => setShowTransferModal(false)}
                    fromLocation="salon"
                    productType="retail"
                    onTransferComplete={() => {
                        setShowTransferModal(false);
                        searchRetailStock(); // Refresh the retail stock data
                        fetchReceivingCount(); // Refresh receiving count
                    }}
                />
            )}

            {/* Stock Transfer History Modal */}
            {showHistoryModal && (
                <StockTransferHistory 
                    isModal={true}
                    onClose={() => setShowHistoryModal(false)}
                    productType="retail"
                    fromLocation="salon"
                    onDataChange={() => {
                        fetchTransferCount(); // Refresh transfer count when data changes
                        fetchReceivingCount(); // Refresh receiving count when data changes
                    }}
                />
            )}

            {/* Stock Receiving Modal */}
            {showReceivingModal && (
                <StockReceiving 
                    isModal={true}
                    onClose={() => setShowReceivingModal(false)}
                    transferFrom="warehouse"
                    productType="retail"
                    onReceiveComplete={() => {
                        setShowReceivingModal(false);
                        searchRetailStock(); // Refresh the retail stock data
                    }}
                />
            )}
        </div>
    );
}

export default Retails
